import { MicIcon, Pause, Square } from "lucide-react";
import { FormattedMessage, IntlProvider } from "react-intl";
import { LoaderCircle } from "~/components/animate-ui/icons/loader-circle";
import { Button } from "~/components/ui/button";
import enMessages from "~/locales/en.json";

interface DemoStateProps {
  label: string;
  description: string;
  children: React.ReactNode;
}

const DemoState = ({ label, description, children }: DemoStateProps) => (
  <div className="flex flex-col gap-3 p-6 border rounded-lg min-w-[280px]">
    <div>
      <span className="text-sm font-semibold">{label}</span>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <div className="flex items-center h-12">{children}</div>
  </div>
);

export const ScribeButtonDemo = () => {
  return (
    <IntlProvider locale="en" messages={enMessages}>
      <div className="min-h-screen bg-background p-12">
        <h1 className="text-2xl font-bold mb-2">
          SKY-930: Scribe Recording Controls
        </h1>
        <p className="text-muted-foreground mb-8">
          Pause/resume recording UI — all 5 visual states
        </p>

        <div className="flex flex-wrap gap-6 items-start">
          {/* State 1: Idle */}
          <DemoState label="1. Idle" description="No active recording session">
            <Button
              size="mini"
              variant="default"
              className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
            >
              <MicIcon className="w-3.5 h-3.5" />
              <span className="text-xs">
                <FormattedMessage
                  id="notes.recording.startScribe"
                  defaultMessage="Start Scribe"
                />
              </span>
            </Button>
          </DemoState>

          {/* State 2: Recording — indicator + pause + stop */}
          <DemoState
            label="2. Recording"
            description="Indicator + Pause + Stop buttons"
          >
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium text-red-600 select-none">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <FormattedMessage
                  id="notes.recording.recording"
                  defaultMessage="Recording"
                />
              </span>
              <Button
                size="mini"
                variant="default"
                className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
              >
                <Pause className="w-3.5 h-3.5" />
                <span className="text-xs">
                  <FormattedMessage
                    id="notes.recording.pause"
                    defaultMessage="Pause"
                  />
                </span>
              </Button>
              <Button
                size="mini"
                variant="destructive"
                className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
              >
                <Square fill="white" className="w-3.5 h-3.5" />
                <span className="text-xs">
                  <FormattedMessage
                    id="notes.recording.stop"
                    defaultMessage="Stop"
                  />
                </span>
              </Button>
            </div>
          </DemoState>

          {/* State 3: Paused — indicator + resume + stop */}
          <DemoState
            label="3. Paused"
            description="Indicator + Resume + Stop buttons"
          >
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 select-none">
                <span className="inline-flex rounded-full h-2 w-2 bg-amber-500" />
                <FormattedMessage
                  id="notes.recording.paused"
                  defaultMessage="Paused"
                />
              </span>
              <Button
                size="mini"
                variant="default"
                className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
              >
                <MicIcon className="w-3.5 h-3.5" />
                <span className="text-xs">
                  <FormattedMessage
                    id="notes.recording.resume"
                    defaultMessage="Resume"
                  />
                </span>
              </Button>
              <Button
                size="mini"
                variant="destructive"
                className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
              >
                <Square fill="white" className="w-3.5 h-3.5" />
                <span className="text-xs">
                  <FormattedMessage
                    id="notes.recording.stop"
                    defaultMessage="Stop"
                  />
                </span>
              </Button>
            </div>
          </DemoState>

          {/* State 4: Processing */}
          <DemoState
            label="4. Processing"
            description="Transcript being processed by AI"
          >
            <Button
              size="mini"
              variant="default"
              disabled
              className="rounded-full flex items-center gap-1.5 justify-center h-8 px-3"
            >
              <LoaderCircle animate className="w-3.5 h-3.5" />
              <span className="text-xs">
                <FormattedMessage
                  id="notes.recording.processing"
                  defaultMessage="Processing..."
                />
              </span>
            </Button>
          </DemoState>
        </div>

        <div className="mt-12 p-4 border rounded-lg max-w-xl">
          <h2 className="font-semibold mb-2">State flow</h2>
          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
            <li>
              <strong>Start Scribe</strong> (blue) — begins recording session
            </li>
            <li>
              <strong>Recording</strong> — pulsing red dot +{" "}
              <strong>Pause</strong> and <strong>Stop</strong> buttons
            </li>
            <li>
              <strong>Paused</strong> — amber dot + <strong>Resume</strong> and{" "}
              <strong>Stop</strong> buttons
            </li>
            <li>
              <strong>Processing...</strong> (disabled) — transcript being
              processed by AI
            </li>
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            Pause/Resume uses Web Speech API stop/start under the hood while
            keeping the transcript session alive.
          </p>
        </div>
      </div>
    </IntlProvider>
  );
};
